import React, { useState, useRef, useEffect } from 'react';
import { Form, Dropdown, InputGroup, Badge } from 'react-bootstrap';
import { MemoryDTO } from '../../../../Book/MemoryBank/memoryBankTypes';
import './IntValueOrVariableSelect.scss';

interface IntValueOrVariableSelectProps {
  intValue?: string | number;
  varValue?: number;
  variables: MemoryDTO[];
  excludeVariableId?: number; // Left operand variable to exclude
  onChange: (data: { intValue?: string, varValue?: number }) => void;
  disabled?: boolean;
  stepIndex: number;
  switchIndex: number;
  placeholder?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
}

export function IntValueOrVariableSelect(props: IntValueOrVariableSelectProps) {
  const {
    intValue,
    varValue,
    variables,
    excludeVariableId,
    onChange,
    disabled,
    stepIndex,
    switchIndex,
    placeholder,
    onKeyDown,
    onPaste,
  } = props;

  const [searchFilter, setSearchFilter] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const isVariableMode = varValue !== undefined && varValue !== null && varValue !== 0;
  const selectedVariable = variables.find(v => v.id === varValue);
  const isEmpty = !isVariableMode && (!intValue || intValue === '');

  // Filter out the left operand variable and apply search filter
  const filteredVariables = variables
    .filter(v => v.id !== excludeVariableId) // Exclude left operand
    .filter(v => v.name.toLowerCase().includes(searchFilter.toLowerCase()));

  useEffect(() => {
    if (showDropdown && searchRef.current) {
      searchRef.current.focus();
    }
  }, [showDropdown]);

  const handleSelectNumber = () => {
    onChange({ intValue: intValue?.toString() || '' });
    setShowDropdown(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSelectVariable = (variable: MemoryDTO) => {
    onChange({ varValue: variable.id });
    setShowDropdown(false);
    setSearchFilter('');
  };

  const handleClearVariable = () => {
    onChange({ intValue: '' });
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ intValue: event.target.value });
  };

  const handleToggleDropdown = () => {
    setShowDropdown(!showDropdown);
    setSearchFilter('');
  };

  return (
    <div className="int-value-variable-select">
      <Dropdown show={showDropdown} onToggle={handleToggleDropdown}>
        <InputGroup size="sm">
          {isVariableMode ? (
            <div className="variable-tag-container">
              <Badge 
                variant="info" 
                className="variable-tag"
                onClick={disabled ? undefined : handleToggleDropdown}
              >
                {selectedVariable?.name || 'Unknown'}
                {!disabled && (
                  <span 
                    className="clear-variable"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearVariable();
                    }}
                  >
                    ×
                  </span>
                )}
              </Badge>
            </div>
          ) : (
            <Form.Control
              ref={inputRef}
              type="text"
              value={intValue || ''}
              placeholder={placeholder}
              onChange={handleInputChange}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              disabled={disabled}
              maxLength={15}
              style={isEmpty ? { backgroundColor: '#fff3cd' } : undefined}
            />
          )}
          
          <Dropdown.Toggle
            variant="outline-secondary"
            id={`dropdown-${stepIndex}-${switchIndex}`}
            disabled={disabled}
            className="dropdown-toggle-btn"
          />
        </InputGroup>

        <Dropdown.Menu className="value-dropdown-menu">
          <div className="search-container">
            <Form.Control
              ref={searchRef}
              type="text"
              size="sm"
              placeholder="Type to filter..."
              value={searchFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchFilter(e.target.value)}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
            />
          </div>

          <Dropdown.Item
            onClick={handleSelectNumber}
            active={!isVariableMode}
            className="set-number-option"
          >
            [Set Number]
            {!isVariableMode && <span className="checkmark">✓</span>}
          </Dropdown.Item>

          <Dropdown.Divider />

          {filteredVariables.length > 0 ? (
            filteredVariables.map((variable) => (
              <Dropdown.Item
                key={variable.id}
                onClick={() => handleSelectVariable(variable)}
                active={variable.id === varValue}
                className="variable-option"
              >
                <Badge variant="info" className="variable-badge">
                  {variable.name}
                </Badge>
                {variable.id === varValue && <span className="checkmark">✓</span>}
              </Dropdown.Item>
            ))
          ) : (
            <div className="no-results">
              {searchFilter ? 'No variables found' : 'No variables available'}
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

